/**
 * 内政管理主页面
 * 君主在此页面管理国内大小事务，整合所有系统
 */

import { useState } from 'react'
import { useShangGameStore } from '../stores/shangGameStore'
import { SHANG_FACTIONS } from '../data/shangDynastyData'
import { CityManagementPage } from './CityManagementPage'
import { DiplomacyPanel } from '../components/diplomacy/DiplomacyPanel'
import { CourtPanel } from '../components/court/CourtPanel'
import { ClanPanel } from '../components/clan/ClanPanel'
import { MilitaryPanel } from '../components/military/MilitaryPanel'
import { RetainerPanel } from '../components/retainer/RetainerPanel'
import { PolicyPanel } from '../components/policy/PolicyPanel'
import { SecretPolicePanel } from '../components/secretPolice/SecretPolicePanel'
import './InternalAffairsPage.css'

interface InternalAffairsPageProps {
  factionId: string
  onBack?: () => void
}

type ActiveTab = 
  | 'dashboard'    // 总览
  | 'court'        // 朝堂
  | 'clan'         // 宗亲
  | 'cities'       // 城池
  | 'military'     // 军队
  | 'retainers'    // 门客
  | 'policies'     // 政策
  | 'secretPolice' // 禁府
  | 'diplomacy'    // 外交
  | 'intelligence' // 情报
  | 'history'      // 日志

export function InternalAffairsPage({ factionId, onBack }: InternalAffairsPageProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard')

  // 获取游戏状态
  const { factionStates: _factionStates } = useShangGameStore()

  // 获取势力信息
  const faction = SHANG_FACTIONS.find(f => f.id === factionId)

  if (!faction) {
    return (
      <div className="internal-affairs-page">
        <div className="empty-state">
          <span className="empty-icon">🏛</span>
          <p>未找到势力信息</p>
        </div>
      </div>
    )
  }

  return (
    <div className="internal-affairs-page">
      {/* 页面头部 */}
      <header className="page-header">
        <div className="header-left">
          {onBack && (
            <button className="back-btn" onClick={onBack}>
              ← 返回
            </button>
          )}
          <div className="faction-info">
            <div
              className="faction-color"
              style={{ backgroundColor: faction.color }}
            />
            <div className="faction-details">
              <h1>{faction.name}</h1>
              <span className="subtitle">内政管理</span>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <button className="primary-btn">
            💰 国库: 10,000
          </button>
          <button className="secondary-btn">
            ⚙️ 设置
          </button>
        </div>
      </header>

      {/* 导航栏 */}
      <nav className="main-nav">
        <div className="nav-tabs">
          <NavTab 
            icon="📊" 
            label="总览" 
            active={activeTab === 'dashboard'} 
            onClick={() => setActiveTab('dashboard')} 
          />
          <NavTab 
            icon="🏛" 
            label="朝堂" 
            active={activeTab === 'court'} 
            onClick={() => setActiveTab('court')} 
          />
          <NavTab 
            icon="👑" 
            label="宗亲" 
            active={activeTab === 'clan'} 
            onClick={() => setActiveTab('clan')} 
          />
          <NavTab 
            icon="🏙" 
            label="城池" 
            active={activeTab === 'cities'} 
            onClick={() => setActiveTab('cities')} 
          />
          <NavTab 
            icon="⚔️" 
            label="军队" 
            active={activeTab === 'military'} 
            onClick={() => setActiveTab('military')} 
          />
          <NavTab 
            icon="🎯" 
            label="门客" 
            active={activeTab === 'retainers'} 
            onClick={() => setActiveTab('retainers')} 
          />
          <NavTab 
            icon="📜" 
            label="政策" 
            active={activeTab === 'policies'} 
            onClick={() => setActiveTab('policies')} 
          />
          <NavTab 
            icon="🔍" 
            label="禁府" 
            active={activeTab === 'secretPolice'} 
            onClick={() => setActiveTab('secretPolice')} 
          />
          <NavTab 
            icon="🌍" 
            label="外交" 
            active={activeTab === 'diplomacy'} 
            onClick={() => setActiveTab('diplomacy')} 
          />
          <NavTab 
            icon="📰" 
            label="情报" 
            active={activeTab === 'intelligence'} 
            onClick={() => setActiveTab('intelligence')} 
          />
          <NavTab 
            icon="📋" 
            label="日志" 
            active={activeTab === 'history'} 
            onClick={() => setActiveTab('history')} 
          />
        </div>
      </nav>

      {/* 内容区域 */}
      <div className="content-area">
        {activeTab === 'dashboard' && <DashboardModule faction={faction} />}
        {activeTab === 'court' && <CourtModule factionId={factionId} />}
        {activeTab === 'clan' && <ClanModule factionId={factionId} />}
        {activeTab === 'cities' && <CitiesModule factionId={factionId} />}
        {activeTab === 'military' && <MilitaryModule factionId={factionId} />}
        {activeTab === 'retainers' && <RetainersModule factionId={factionId} />}
        {activeTab === 'policies' && <PoliciesModule factionId={factionId} />}
        {activeTab === 'secretPolice' && <SecretPoliceModule factionId={factionId} />}
        {activeTab === 'diplomacy' && <DiplomacyModule factionId={factionId} />}
        {activeTab === 'intelligence' && <IntelligenceModule />}
        {activeTab === 'history' && <HistoryModule />}
      </div>
    </div>
  )
}

// 导航标签组件
interface NavTabProps {
  icon: string
  label: string
  active: boolean
  onClick: () => void
}

function NavTab({ icon, label, active, onClick }: NavTabProps) {
  return (
    <button 
      className={`nav-tab ${active ? 'active' : ''}`}
      onClick={onClick}
      title={label}
    >
      <span className="tab-icon">{icon}</span>
      <span className="tab-label">{label}</span>
    </button>
  )
}

// 总览模块
function DashboardModule({ faction: _faction }: { faction: any }) {
  return (
    <div className="module dashboard-module">
      <h2>📊 国家总览</h2>
      <div className="overview-grid">
        <StatCard icon="🏛" title="国力" value="8,520" change="+5%" />
        <StatCard icon="👥" title="民心" value="72/100" change="-3%" />
        <StatCard icon="⚖️" title="稳定" value="85/100" change="0%" />
        <StatCard icon="📰" title="情报" value="高" info="3新密报" />
        <StatCard icon="💰" title="国库" value="10,000" change="+2%" />
        <StatCard icon="🌾" title="粮食" value="50,000" change="+8%" />
      </div>

      <div className="emergency-section">
        <h3>🚨 紧急事务</h3>
        <div className="emergency-list">
          <EmergencyItem 
            title="东夷入侵边境" 
            severity="high" 
            time="1小时前" 
          />
          <EmergencyItem 
            title="粮价上涨" 
            severity="medium" 
            time="3小时前" 
          />
          <EmergencyItem 
            title="官员贪污" 
            severity="low" 
            time="5小时前" 
          />
        </div>
      </div>
    </div>
  )
}

// 朝堂模块
function CourtModule({ factionId }: { factionId: string }) {
  return (
    <div className="module court-module">
      <h2>🏛 朝堂</h2>
      <CourtPanel factionId={factionId} />
    </div>
  )
}

// 宗亲模块
function ClanModule({ factionId }: { factionId: string }) {
  return (
    <div className="module clan-module">
      <h2>👑 宗亲</h2>
      <ClanPanel factionId={factionId} />
    </div>
  )
}

// 城池模块
function CitiesModule({ factionId }: { factionId: string }) {
  return (
    <div className="module cities-module">
      <h2>🏙 城池管理</h2>
      <CityManagementPage factionId={factionId} embedded={true} />
    </div>
  )
}

// 军队模块
function MilitaryModule({ factionId }: { factionId: string }) {
  return (
    <div className="module military-module">
      <h2>⚔️ 军队</h2>
      <MilitaryPanel factionId={factionId} />
    </div>
  )
}

// 门客模块
function RetainersModule({ factionId }: { factionId: string }) {
  return (
    <div className="module retainers-module">
      <h2>🎯 门客</h2>
      <RetainerPanel factionId={factionId} />
    </div>
  )
}

// 政策模块
function PoliciesModule({ factionId }: { factionId: string }) {
  return (
    <div className="module policies-module">
      <h2>📜 政策</h2>
      <PolicyPanel factionId={factionId} />
    </div>
  )
}

// 禁府模块
function SecretPoliceModule({ factionId }: { factionId: string }) {
  return (
    <div className="module secret-police-module">
      <h2>🔒 禁府</h2>
      <SecretPolicePanel factionId={factionId} />
    </div>
  )
}

// 外交模块
function DiplomacyModule({ factionId }: { factionId: string }) {
  return (
    <div className="module diplomacy-module">
      <h2>🌍 外交</h2>
      <DiplomacyPanel factionId={factionId} />
    </div>
  )
}

// 情报模块
function IntelligenceModule() {
  return (
    <div className="module intelligence-module">
      <h2>📰 情报</h2>
      <p>情报系统开发中...</p>
    </div>
  )
}

// 日志模块
function HistoryModule() {
  return (
    <div className="module history-module">
      <h2>📋 日志</h2>
      <p>日志系统开发中...</p>
    </div>
  )
}

// 统计卡片组件
interface StatCardProps {
  icon: string
  title: string
  value: string
  change?: string
  info?: string
}

function StatCard({ icon, title, value, change, info }: StatCardProps) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        {change && (
          <div className={`stat-change ${change.startsWith('+') ? 'positive' : 'negative'}`}>
            {change}
          </div>
        )}
        {info && (
          <div className="stat-info">{info}</div>
        )}
      </div>
    </div>
  )
}

// 紧急事务项组件
interface EmergencyItemProps {
  title: string
  severity: 'high' | 'medium' | 'low'
  time: string
}

function EmergencyItem({ title, severity, time }: EmergencyItemProps) {
  const severityColor = {
    high: '#f44336',
    medium: '#ff9800',
    low: '#4caf50'
  }

  return (
    <div className="emergency-item">
      <div 
        className="emergency-severity" 
        style={{ backgroundColor: severityColor[severity] }}
      />
      <div className="emergency-content">
        <div className="emergency-title">{title}</div>
        <div className="emergency-time">{time}</div>
      </div>
      <button className="emergency-action">处理</button>
    </div>
  )
}
